import { Radio } from "@mantine/core";
import {
  PALETTE_DESCRIPTIONS,
  PALETTE_GROUNDS,
  PALETTE_LABELS,
  PALETTES,
  readableOn,
  TYPEFACE_DESCRIPTIONS,
  TYPEFACE_LABELS,
  TYPEFACES,
  type Brand,
  type Palette,
  type Typeface,
} from "@how/website-core";
import { PALETTE_INK, TYPEFACE_STACKS } from "../look-values";
import { mediaUrl } from "../media-queries";
import { useTenantRecord } from "../../tenant/queries";
import { usePublicHost } from "../website-queries";

/*
 * Website → Look in the studio design: the palette and the typeface chosen from cards that show them, and the site
 * drawn large beside the choices, in the church's own ground, ink, accent, face and logo.
 */

/** The four grounds as cards: each a swatch of the ground with "Aa" in its ink and a button in the accent. */
export function PalettePicker({
  brand,
  disabled,
  onChange,
}: {
  readonly brand: Brand;
  readonly disabled: boolean;
  readonly onChange: (palette: Palette) => void;
}) {
  return (
    <Radio.Group
      value={brand.palette}
      onChange={(value) => {
        onChange(value as Palette);
      }}
      aria-labelledby="ws-palette-heading"
      name="palette"
    >
      <div className="ws-choices">
        {PALETTES.map((palette) => (
          <Radio.Card
            key={palette}
            value={palette}
            disabled={disabled}
            className="ws-choice"
            aria-label={`${PALETTE_LABELS[palette]}: ${PALETTE_DESCRIPTIONS[palette]}`}
          >
            <span
              className="ws-choice__swatch"
              style={{ background: PALETTE_GROUNDS[palette], color: PALETTE_INK[palette] }}
              aria-hidden
            >
              <span className="ws-choice__aa">Aa</span>
              <span className="ws-choice__pill" style={{ background: brand.accent }} />
            </span>
            <span className="ws-choice__body">
              <span className="ws-choice__top">
                <Radio.Indicator disabled={disabled} aria-hidden size="xs" />
                <span className="ws-choice__label">{PALETTE_LABELS[palette]}</span>
              </span>
              <span className="ws-choice__description">{PALETTE_DESCRIPTIONS[palette]}</span>
            </span>
          </Radio.Card>
        ))}
      </div>
    </Radio.Group>
  );
}

/** The three faces as cards, each set in itself. */
export function TypefacePicker({
  brand,
  disabled,
  onChange,
}: {
  readonly brand: Brand;
  readonly disabled: boolean;
  readonly onChange: (typeface: Typeface) => void;
}) {
  return (
    <Radio.Group
      value={brand.typeface}
      onChange={(value) => {
        onChange(value as Typeface);
      }}
      aria-labelledby="ws-typeface-heading"
      name="typeface"
    >
      <div className="ws-choices ws-choices--three">
        {TYPEFACES.map((face) => (
          <Radio.Card
            key={face}
            value={face}
            disabled={disabled}
            className="ws-choice"
            aria-label={`${TYPEFACE_LABELS[face]}: ${TYPEFACE_DESCRIPTIONS[face]}`}
          >
            <span
              className="ws-choice__face"
              style={{ fontFamily: TYPEFACE_STACKS[face] }}
              aria-hidden
            >
              Aa
            </span>
            <span className="ws-choice__body">
              <span className="ws-choice__top">
                <Radio.Indicator disabled={disabled} aria-hidden size="xs" />
                <span className="ws-choice__label">{TYPEFACE_LABELS[face]}</span>
              </span>
              <span className="ws-choice__description">{TYPEFACE_DESCRIPTIONS[face]}</span>
            </span>
          </Radio.Card>
        ))}
      </div>
    </Radio.Group>
  );
}

/**
 * The church's home page, large: the header with the logo or the name and the menu, a headline, the accented action
 * and the outlined one, and a row of cards. Drawn from the brand being edited, so it changes as the choices do.
 */
export function SitePreview({ brand }: { readonly brand: Brand }) {
  const churchName = useTenantRecord().data?.name ?? "Your church";
  const address = usePublicHost().data ?? "your-church.org";
  const ground = PALETTE_GROUNDS[brand.palette];
  const ink = PALETTE_INK[brand.palette];
  const font = TYPEFACE_STACKS[brand.typeface];
  return (
    <div className="ws-preview" aria-hidden>
      <div className="ws-thumb__bar">
        <span className="ws-thumb__dots">
          <i />
          <i />
          <i />
        </span>
        <span className="ws-thumb__address">{address}</span>
      </div>
      <div
        className="ws-preview__page"
        style={{ background: ground, color: ink, fontFamily: font }}
      >
        <div className="ws-preview__nav">
          {brand.logoPath === undefined ? (
            <span className="ws-preview__name">{churchName}</span>
          ) : (
            <img
              src={mediaUrl(brand.logoPath)}
              alt=""
              style={{ width: Math.min(brand.logoWidth, 160), maxHeight: 40, objectFit: "contain" }}
            />
          )}
          <span className="ws-preview__links">
            <span>About</span>
            <span>Visit</span>
            <span>Sermons</span>
          </span>
        </div>
        <div className="ws-preview__hero">
          <span className="ws-preview__eyebrow" style={{ color: ink }}>
            Sundays at 10am
          </span>
          <span className="ws-preview__headline">You belong here</span>
          <span className="ws-preview__lead">Every Sunday, and everyone welcome.</span>
          <span className="ws-preview__actions">
            <span
              className="ws-preview__primary"
              style={{ background: brand.accent, color: readableOn(brand.accent) }}
            >
              Plan a visit
            </span>
            <span className="ws-preview__secondary" style={{ borderColor: ink }}>
              About us
            </span>
          </span>
        </div>
        <div className="ws-preview__cards">
          {["Groups", "Kids", "Give"].map((label) => (
            <span
              key={label}
              className="ws-preview__card"
              style={{ borderColor: `color-mix(in srgb, ${ink} 14%, transparent)` }}
            >
              <span
                className="ws-preview__card-dot"
                style={{ background: `color-mix(in srgb, ${brand.accent} 30%, ${ground})` }}
              />
              {label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
